import type {
  FailoverPolicy,
  CommercialAvailability,
  MediaOwnerType,
  ProductCardStatus,
  PricingMode,
  RuleType,
  SortOption,
} from './types';

export interface SelectOption {
  value: string;
  label: string;
}

/** Sort presets for the product list/search toolbar. */
export interface ProductSortPreset {
  value: string;
  label: string;
  sort: SortOption[];
}

export const PRODUCT_SORT_PRESETS: ProductSortPreset[] = [
  { value: 'name-asc', label: 'Название (А→Я)', sort: [{ property: 'name', direction: 'ASC' }] },
  { value: 'name-desc', label: 'Название (Я→А)', sort: [{ property: 'name', direction: 'DESC' }] },
  { value: 'price-asc', label: 'Цена (по возрастанию)', sort: [{ property: 'price', direction: 'ASC' }] },
  { value: 'price-desc', label: 'Цена (по убыванию)', sort: [{ property: 'price', direction: 'DESC' }] },
  { value: 'newest', label: 'Сначала новые', sort: [{ property: 'createdAt', direction: 'DESC' }] },
];

export const STOCK_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'Все товары' },
  { value: 'IN_STOCK', label: 'В наличии' },
  { value: 'OUT_OF_STOCK', label: 'Нет в наличии' },
  { value: 'CHECK_AVAILABILITY', label: 'Уточняйте наличие' },
];

/** Availability select (3 states) used in the product create/edit and quick-stock forms. */
export const STOCK_STATUS_OPTIONS: SelectOption[] = [
  { value: 'IN_STOCK', label: 'В наличии' },
  { value: 'OUT_OF_STOCK', label: 'Нет в наличии' },
  { value: 'CHECK_AVAILABILITY', label: 'Уточняйте наличие' },
];

export const STOCK_STATUS_LABELS: Record<string, string> = {
  IN_STOCK: 'В наличии',
  OUT_OF_STOCK: 'Нет в наличии',
  CHECK_AVAILABILITY: 'Уточняйте наличие',
};

export const PRODUCT_CARD_STATUS_OPTIONS: { value: ProductCardStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Черновик / на модерации' },
  { value: 'ACTIVE', label: 'Готов к публикации' },
  { value: 'ARCHIVED', label: 'В архиве' },
];

export const PRODUCT_CARD_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Черновик',
  ACTIVE: 'Активен',
  ARCHIVED: 'В архиве',
};

// NOTE: 'ARCHIVED' is intentionally excluded from this list.
// Archiving must be performed via the publication lifecycle (ProductPublicationCard →
// "Архивировать" button → POST /archive), which cascades cardStatus + lifecycleStatus
// and unpublishes all channels. Setting commercialAvailability=ARCHIVED through the
// state-update endpoint alone produces a ghost-archived state (product removed from
// storefront but still shown as "Active + Published" in back-office) and blocks
// re-publishing with a ConflictException.
export const COMMERCIAL_AVAILABILITY_OPTIONS: { value: CommercialAvailability; label: string }[] = [
  { value: 'AVAILABLE', label: 'Доступен к покупке' },
  { value: 'CHECK_AVAILABILITY', label: 'Уточняйте наличие' },
  { value: 'PREORDER', label: 'Предзаказ' },
  { value: 'COMING_SOON', label: 'Скоро в продаже' },
  { value: 'OUT_OF_STOCK', label: 'Нет в наличии' },
  { value: 'TEMPORARILY_UNAVAILABLE', label: 'Временно недоступен' },
];

export const COMMERCIAL_AVAILABILITY_LABELS: Record<string, string> = Object.fromEntries(
  COMMERCIAL_AVAILABILITY_OPTIONS.map((o) => [o.value, o.label]),
);

export function productCardStatusColor(status?: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DRAFT':
      return 'warning';
    default:
      return 'default';
  }
}

export function commercialAvailabilityColor(
  status?: string,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'AVAILABLE':
    case 'PREORDER':
      return 'success';
    // CHECK_AVAILABILITY = "уточняйте наличие" — buyer uncertainty, not a positive state
    case 'CHECK_AVAILABILITY':
    case 'COMING_SOON':
      return 'warning';
    case 'OUT_OF_STOCK':
    case 'TEMPORARILY_UNAVAILABLE':
      return 'error';
    default:
      return 'default';
  }
}

/** Colour bucket for the availability chip. OUT_OF_STOCK is error (requires restocking action). */
export function stockStatusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'IN_STOCK':
      return 'success';
    case 'CHECK_AVAILABILITY':
      return 'warning';
    case 'OUT_OF_STOCK':
      return 'error';
    default:
      return 'default';
  }
}

export const PRICE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активна',
  NO_PRICE: 'Нет цены',
  PENDING: 'На проверке',
  BLOCKED: 'Заблокирована',
  EXPIRED: 'Истекла',
};

/** A muted/positive/etc. colour bucket for a price status chip. */
export function priceStatusColor(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'BLOCKED':
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
}

// ── Publication / lifecycle (status = colour + label, ru-RU) ─────────────────
export const LIFECYCLE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активен',
  ARCHIVED: 'В архиве',
  COMING_SOON: 'Скоро в продаже',
  PREORDER: 'Предзаказ',
  TEMPORARILY_UNAVAILABLE: 'Временно недоступен',
};

/** Colour bucket for the lifecycle chip (ARCHIVED → muted, transitional states → warning). */
export function lifecycleStatusColor(status?: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'COMING_SOON':
    case 'PREORDER':
    case 'TEMPORARILY_UNAVAILABLE':
      return 'warning';
    default:
      return 'default';
  }
}

export const PUBLICATION_STATUS_LABELS: Record<string, string> = {
  PUBLISHED: 'Опубликован',
  UNPUBLISHED: 'Черновик',
};

/** Colour bucket for a per-channel publication chip. */
export function publicationStatusColor(status?: string): 'success' | 'default' {
  return status === 'PUBLISHED' ? 'success' : 'default';
}

export const MEDIA_OWNER_TYPE: MediaOwnerType = 'PRODUCT';

export const PRICING_MODE_OPTIONS: { value: PricingMode; label: string }[] = [
  { value: 'INTERNAL', label: 'Внутренний движок цен' },
  { value: 'EXTERNAL', label: 'Внешний провайдер' },
  { value: 'HYBRID_INTERNAL_FIRST', label: 'Гибрид (внутренний приоритет)' },
  { value: 'HYBRID_EXTERNAL_FIRST', label: 'Гибрид (внешний приоритет)' },
];

export const FAILOVER_POLICY_OPTIONS: { value: FailoverPolicy; label: string }[] = [
  { value: 'FALLBACK_TO_INTERNAL', label: 'Откат на внутренний движок' },
  { value: 'RETURN_CACHED', label: 'Вернуть кэш' },
  { value: 'FAIL_FAST', label: 'Сразу ошибка' },
];

export const RULE_TYPE_OPTIONS: { value: RuleType; label: string }[] = [
  { value: 'PRODUCT_DISCOUNT', label: 'Скидка на товар' },
  { value: 'CUSTOMER_DISCOUNT', label: 'Скидка клиенту' },
  { value: 'PROMO_CODE', label: 'Промокод' },
  { value: 'LOYALTY', label: 'Лояльность' },
  { value: 'DELIVERY_FEE', label: 'Стоимость доставки' },
  { value: 'TAX', label: 'Налог' },
];

export const RULE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  RULE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
